import { useEffect, useState, type RefObject } from "react";

// Shared modal a11y behaviour: close on Escape (unless locked, e.g. mid-submit), trap Tab focus
// within the modal card, and restore focus to the element that opened the modal on unmount.
export function useModalDismiss(cardRef: RefObject<HTMLElement | null>, onClose: () => void, locked = false) {
  // Capture the opener during the first render (before the modal's autoFocus moves focus into it),
  // so we can hand focus back to the trigger when the modal closes.
  const [opener] = useState<HTMLElement | null>(
    () => (typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null),
  );

  // Restore focus to the opener on unmount only (deps: [] — not affected by `locked` toggles).
  useEffect(() => () => opener?.focus?.(), [opener]);

  // Escape-to-close (guarded while locked) + Tab focus-trap.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (!locked) onClose();
        return;
      }
      if (e.key !== "Tab" || !cardRef.current) return;
      const nodes = cardRef.current.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cardRef, onClose, locked]);
}
